/**
 * A FILE through the share sheet — an image card, a CSV — where
 * `outbound.ts`'s `share()` carries text and links only.
 *
 * ── NATIVE: THROUGH THE CACHE DIRECTORY ────────────────────────────────────
 * The Share plugin takes `files` as URIs, never bytes, so the blob is written
 * to `Directory.Cache` first and its URI handed over. The cache directory and
 * not `Data`: the file exists only so another app can read it once, Android
 * may reclaim it whenever it likes, and nothing in the app ever reads it back.
 * One fixed folder (`shared/`), overwritten by name, so repeated shares do not
 * pile up copies.
 *
 * ── WEB: THE WEB SHARE API, ELSE A DOWNLOAD ────────────────────────────────
 * A phone browser shares a `File` with `navigator.share({ files })` when
 * `navigator.canShare` says it can; a desktop browser generally cannot, and
 * gets an ordinary download instead — which is what "share" means there.
 *
 * Never throws: every outcome is one of {@link ShareFileOutcome}, so a screen
 * says one sentence per outcome and nothing reaches the error reporter for a
 * person who simply closed the sheet.
 */
import { isNative } from "./platform";

export interface ShareFileContent {
  readonly blob: Blob;
  /** The name the receiving app sees, extension included. */
  readonly filename: string;
  /**
   * Words that travel with the file — a caption, and the share link while
   * links are on (`D257`, `D260`). Android hands it over as `EXTRA_TEXT`;
   * the web as the share data's `text`. Absent, the file goes alone.
   */
  readonly text?: string;
  /** The share sheet's title on Android, the share data's on the web. */
  readonly title?: string;
}

/**
 * `shared` — the sheet opened and was used (or, on Android, at least opened:
 * the plugin cannot tell a send from a back-out on every app); `downloaded` —
 * the web fallback saved the file; `dismissed` — the person closed the
 * share sheet or backed out of the chooser; `failed` — nothing could be done.
 */
export type ShareFileOutcome = "dismissed" | "downloaded" | "failed" | "shared";

/**
 * The outcome and, when it is `shared`, WHERE TO as far as the platform says
 * (`D258`): Android's chooser reports the package the person picked
 * (`com.whatsapp`) through the plugin's `activityType`, and nothing when it
 * could not tell (`unknown`); the web's sheet never says (`web_share`); the
 * desktop fallback is a `download`. `null` for an outcome that sent nothing.
 * Never guessed.
 */
export interface ShareFileResult {
  readonly channel: string | null;
  readonly outcome: ShareFileOutcome;
}

/** The plugin's rejection when the person backs out of the chooser. */
const CANCELED = /cancel/i;

/** The blob as base64, the encoding Filesystem's binary write takes. */
function base64Of(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.slice(url.indexOf(",") + 1));
    };
    reader.readAsDataURL(blob);
  });
}

async function shareNative(
  content: ShareFileContent
): Promise<ShareFileResult> {
  const { Directory, Filesystem } = await import("@capacitor/filesystem");
  const { Share } = await import("@capacitor/share");
  const written = await Filesystem.writeFile({
    data: await base64Of(content.blob),
    directory: Directory.Cache,
    path: `shared/${content.filename}`,
    recursive: true,
  });
  try {
    const result = await Share.share({
      files: [written.uri],
      ...(content.text === undefined ? {} : { text: content.text }),
      ...(content.title === undefined ? {} : { title: content.title }),
    });
    const channel = result.activityType?.trim() ?? "";
    return { channel: channel === "" ? "unknown" : channel, outcome: "shared" };
  } catch (error) {
    // Backing out of the chooser is a choice, not a failure: no toast.
    if (error instanceof Error && CANCELED.test(error.message)) {
      return { channel: null, outcome: "dismissed" };
    }
    throw error;
  }
}

/** A plain download through an object URL, revoked on the next tick. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function shareWeb(content: ShareFileContent): Promise<ShareFileResult> {
  const file = new File([content.blob], content.filename, {
    type: content.blob.type,
  });
  const data: ShareData = {
    files: [file],
    ...(content.text === undefined ? {} : { text: content.text }),
    ...(content.title === undefined ? {} : { title: content.title }),
  };
  if (typeof navigator.canShare === "function" && navigator.canShare(data)) {
    try {
      await navigator.share(data);
      return { channel: "web_share", outcome: "shared" };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return { channel: null, outcome: "dismissed" };
      }
      // Refused for another reason (no user activation left, a policy): the
      // download below still gets the file to the person.
    }
  }
  downloadBlob(content.blob, content.filename);
  return { channel: "download", outcome: "downloaded" };
}

/** {@link shareFile} with the channel the share went to (`D258`). */
export async function shareFileDetailed(
  content: ShareFileContent
): Promise<ShareFileResult> {
  try {
    return isNative() ? await shareNative(content) : await shareWeb(content);
  } catch {
    return { channel: null, outcome: "failed" };
  }
}

export async function shareFile(
  content: ShareFileContent
): Promise<ShareFileOutcome> {
  return (await shareFileDetailed(content)).outcome;
}
